import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Select, {type SelectChangeEvent} from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {useTheme} from "@mui/material/styles";
import * as React from "react";
import {useTranslation} from "react-i18next";
import {chartPalette} from "../../components/chartColors";
import {InsightsLegend, LegendEntry} from "../../kit/InsightsLegend";
import {InsightsColumn, InsightsTable} from "../../kit/InsightsTable";
import {SeriesCard} from "../../kit/SeriesCard";
import {CHART_HEIGHT} from "../../kit/insightsChart";
import type {InsightsLoading} from "../../kit/insightsLoading";
import type {CrmRatesTimeSeriesV4} from "../../models/crm-v4";
import CrmSeriesLinePlot, {type SeriesPlotRow} from "./CrmSeriesLinePlot";
import {useBucketLabels} from "./crmTrendBuckets";

interface RatesTrendCardProps {
    data?: CrmRatesTimeSeriesV4;
    loading?: InsightsLoading;
}

export function RatesTrendCard({data, loading}: RatesTrendCardProps) {
    const {t, i18n} = useTranslation();
    const theme = useTheme();
    const [excludedRateIds, setExcludedRateIds] = React.useState<string[]>([]);

    const locale = i18n.resolvedLanguage || i18n.language;
    const allSeries = data?.series ?? [];
    // A rate nobody sold in this period is noise in the picker, not an option worth offering.
    const availableSeries = allSeries.filter(series => series.values.some(value => value != null && value > 0));
    const availableIds = new Set(availableSeries.map(series => series.id));
    const selectedSeries = availableSeries.filter(series => !excludedRateIds.includes(series.id));
    const selectedIds = selectedSeries.map(series => series.id);
    const hasData = availableSeries.length > 0;

    const points = data?.points ?? [];
    const buckets = useBucketLabels(points, data?.granularity);
    const valueFormatter = new Intl.NumberFormat(locale, {maximumFractionDigits: 0});
    const axisFormatter = new Intl.NumberFormat(locale, {notation: "compact", maximumFractionDigits: 1});
    const formatNights = (value: number) => `${valueFormatter.format(value)} ${t("nights")}`;

    const palette = chartPalette(theme);
    const colorByRateId = new Map(availableSeries.map((series, index) => [
        series.id,
        palette[index % palette.length],
    ]));

    const rows: SeriesPlotRow[] = buckets.map((bucket, index) => {
        const row: SeriesPlotRow = {key: bucket.key, label: bucket.label, full: bucket.full};

        selectedSeries.forEach(series => {
            row[series.id] = series.values[index] ?? null;
        });

        return row;
    });

    const plotted = selectedSeries.map(series => ({
        id: series.id,
        label: series.name,
        color: colorByRateId.get(series.id) ?? theme.palette.text.secondary,
    }));

    const columns: InsightsColumn<SeriesPlotRow>[] = [
        {key: "bucket", label: t("Time range"), kind: "label", cell: row => row.full},
        ...plotted.map(series => ({
            key: series.id,
            label: series.label,
            kind: "value" as const,
            cell: (row: SeriesPlotRow) => {
                const value = row[series.id];

                return value == null ? "—" : formatNights(Number(value));
            },
        })),
    ];

    const handleSelectionChange = (event: SelectChangeEvent<string[]>) => {
        const nextSelected = typeof event.target.value === "string"
            ? event.target.value.split(",")
            : event.target.value;

        setExcludedRateIds(previous => [
            ...previous.filter(id => !availableIds.has(id)),
            ...availableSeries.filter(series => !nextSelected.includes(series.id)).map(series => series.id),
        ]);
    };

    const renderSelection = (ids: string[]) => {
        if (ids.length === availableSeries.length) return t("All rates");
        if (ids.length === 0) return t("No rates selected");
        if (ids.length === 1) return availableSeries.find(series => series.id === ids[0])?.name ?? ids[0];
        return t("{{count}} rates", {count: ids.length});
    };

    const emptyMessage = allSeries.length === 0
        ? t("No accommodation rates are configured")
        : !hasData
            ? t("No data in the selected period")
            : selectedSeries.length === 0
                ? t("Select at least one rate")
                : undefined;

    return (
        <SeriesCard
            title={t("Rates over time")}
            hint={t("Occupied room nights for each accommodation rate in each period")}
            loading={loading}
            actions={hasData ? (
                <FormControl size={"small"} sx={{minWidth: 220}}>
                    <InputLabel id={"insights-rate-series-select-label"}>{t("Rates")}</InputLabel>
                    <Select<string[]>
                        multiple
                        labelId={"insights-rate-series-select-label"}
                        label={t("Rates")}
                        value={selectedIds}
                        onChange={handleSelectionChange}
                        renderValue={renderSelection}
                        disabled={loading != null}
                        sx={{height: 36, "& .MuiSelect-select": {py: 0.75}}}
                        MenuProps={{PaperProps: {sx: {maxHeight: 360}}}}
                    >
                        {availableSeries.map(series => (
                            <MenuItem key={series.id} value={series.id}>
                                <Checkbox checked={selectedIds.includes(series.id)}/>
                                <ListItemText primary={series.name}/>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            ) : undefined}
            empty={emptyMessage == null ? undefined : (
                <Stack alignItems={"center"} justifyContent={"center"} sx={{height: CHART_HEIGHT, px: 3}}>
                    <Typography variant={"body2"} color={"text.secondary"} textAlign={"center"}>
                        {emptyMessage}
                    </Typography>
                </Stack>
            )}
            legend={(
                <InsightsLegend>
                    {plotted.map(series => (
                        <LegendEntry key={series.id} color={series.color} label={series.label}/>
                    ))}
                </InsightsLegend>
            )}
            table={<InsightsTable label={t("Rates over time")} columns={columns} rows={rows} rowKey={row => row.key}/>}
            chart={(
                <CrmSeriesLinePlot
                    rows={rows}
                    series={plotted}
                    formatValue={formatNights}
                    formatTick={value => axisFormatter.format(value)}
                />
            )}
        />
    );
}

export default RatesTrendCard;
